// src/shared/dsp/telemetryDsp.ts
// ────────────────────────────────────────────────────────────────────────────
// Client-side DSP pipeline for IoT telemetry signals.
//
// Four processing stages applied in order:
//   1. Glitch gate    — median filter removes spikes / transient outliers
//   2. Smoothing      — EMA (exponential moving average) reduces nervousness
//   3. Interpolation  — cubic-Hermite resampling fills resolution gaps
//   4. Dead-zone      — suppresses jitter below a configurable threshold
//
// All functions are pure — no side-effects, no DOM, safe for workers.
// Operates on TimeSeriesPoint[] (ts: epoch-ms, value: number).
// ────────────────────────────────────────────────────────────────────────────

export interface TimeSeriesPoint {
  ts:    number;  // epoch-ms
  value: number;
}

/* ═══════════════════════════════════════════════════════════════════════
   DSP Preset — the four knobs exposed to the UI
   ═══════════════════════════════════════════════════════════════════════ */

export interface DspPreset {
  /** Median filter window radius (0 = off, 1 = 3-tap, 2 = 5-tap, …). */
  glitchRadius:   number;
  /** EMA smoothing factor α ∈ [0, 1].  0 = raw, 1 = frozen.
   *  Typical good range: 0.05–0.40. */
  smoothingAlpha: number;
  /** Interpolation density multiplier (1 = off, 2–8 = upsample). */
  interpFactor:   number;
  /** Dead-zone half-width — deltas < this are suppressed (0 = off). */
  deadZone:       number;
}

export const DSP_DEFAULTS: Readonly<DspPreset> = {
  glitchRadius:   1,
  smoothingAlpha: 0.18,
  interpFactor:   4,
  deadZone:       0.05,
};

/* ═══════════════════════════════════════════════════════════════════════
   Stage 1 — Median filter (glitch removal)
   ═══════════════════════════════════════════════════════════════════════ */

function medianFilter(
  pts: TimeSeriesPoint[],
  radius: number,
): TimeSeriesPoint[] {
  if (radius <= 0 || pts.length < 3) return pts;
  const window = 2 * radius + 1;
  const out: TimeSeriesPoint[] = new Array(pts.length);
  const buf: number[] = new Array(window);

  for (let i = 0; i < pts.length; i++) {
    let k = 0;
    for (let j = i - radius; j <= i + radius; j++) {
      const idx = Math.max(0, Math.min(pts.length - 1, j));
      buf[k++] = pts[idx].value;
    }
    // sort numerically — small window so insertion sort is fine
    for (let a = 1; a < k; a++) {
      const v = buf[a];
      let b = a - 1;
      while (b >= 0 && buf[b] > v) { buf[b + 1] = buf[b]; b--; }
      buf[b + 1] = v;
    }
    out[i] = { ts: pts[i].ts, value: buf[radius] };
  }
  return out;
}

/* ═══════════════════════════════════════════════════════════════════════
   Stage 2 — Time-weighted EMA smoother (reduce nervousness)

   Unlike a fixed-α EMA, the effective smoothing factor adapts to the
   actual time gap between consecutive samples:

     α_eff = 1 − (1−α)^(Δt / Δt_ref)

   where Δt_ref is the median sampling interval.  This gives physically
   correct smoothing regardless of irregular sample spacing — dense
   bursts are smoothed more, long gaps let the new value dominate.
   ═══════════════════════════════════════════════════════════════════════ */

function emaSmooth(
  pts: TimeSeriesPoint[],
  alpha: number,
): TimeSeriesPoint[] {
  if (alpha <= 0 || pts.length === 0) return pts;
  const a = Math.min(alpha, 0.99);

  // Reference interval = median of up to first 20 inter-sample gaps.
  const sampleN = Math.min(pts.length - 1, 20);
  let dtRef = 1; // fallback: degenerate to standard per-sample EMA
  if (sampleN > 0) {
    const gaps: number[] = new Array(sampleN);
    for (let i = 0; i < sampleN; i++) {
      gaps[i] = Math.max(1, pts[i + 1].ts - pts[i].ts);
    }
    gaps.sort((x, y) => x - y);
    dtRef = gaps[Math.floor(gaps.length / 2)];
  }

  const logRetain = Math.log(1 - a); // ln(1−α), computed once
  const out: TimeSeriesPoint[] = new Array(pts.length);
  out[0] = { ...pts[0] };
  for (let i = 1; i < pts.length; i++) {
    const dt = Math.max(1, pts[i].ts - pts[i - 1].ts);
    // α_eff = 1 − (1−α)^(dt/dtRef)  =  1 − e^(ln(1−α)·dt/dtRef)
    const aEff = 1 - Math.exp(logRetain * dt / dtRef);
    out[i] = {
      ts:    pts[i].ts,
      value: out[i - 1].value * (1 - aEff) + pts[i].value * aEff,
    };
  }
  return out;
}

/* ═══════════════════════════════════════════════════════════════════════
   Stage 3 — Monotone cubic Hermite interpolation (Fritsch-Carlson 1980)

   Replaces basic Catmull-Rom with Fritsch-Carlson monotone cubic
   interpolation.  The key difference: tangent slopes are clamped so
   the interpolated curve never overshoots between sample points.
   This eliminates the ringing / artificial oscillation that standard
   Catmull-Rom produces on flat-ish sensor signals (temperature,
   humidity), giving a smooth curve that faithfully follows the data.
   ═══════════════════════════════════════════════════════════════════════ */

function hermiteInterp(
  pts: TimeSeriesPoint[],
  factor: number,
): TimeSeriesPoint[] {
  if (factor <= 1 || pts.length < 2) return pts;

  const n = pts.length;

  // Step 1: Secant slopes δ_k = Δv / Δt for each interval
  const delta: number[] = new Array(n - 1);
  for (let i = 0; i < n - 1; i++) {
    const dt = pts[i + 1].ts - pts[i].ts;
    delta[i] = dt !== 0 ? (pts[i + 1].value - pts[i].value) / dt : 0;
  }

  // Step 2: Initial tangent slopes — arithmetic mean of adjacent secants
  const m: number[] = new Array(n);
  m[0] = delta[0];
  m[n - 1] = delta[n - 2];
  for (let i = 1; i < n - 1; i++) {
    if (delta[i - 1] * delta[i] <= 0) {
      m[i] = 0; // sign change → flat tangent (local extremum)
    } else {
      m[i] = (delta[i - 1] + delta[i]) / 2;
    }
  }

  // Step 3: Fritsch-Carlson monotonicity constraint
  //   Clamp α² + β² ≤ 9 where α = m[k]/δ[k], β = m[k+1]/δ[k]
  for (let i = 0; i < n - 1; i++) {
    if (Math.abs(delta[i]) < 1e-12) {
      m[i] = 0;
      m[i + 1] = 0;
      continue;
    }
    const ak = m[i] / delta[i];
    const bk = m[i + 1] / delta[i];
    const mag = ak * ak + bk * bk;
    if (mag > 9) {
      const s = 3 / Math.sqrt(mag);
      m[i]     = s * ak * delta[i];
      m[i + 1] = s * bk * delta[i];
    }
  }

  // Step 4: Generate interpolated points using Hermite basis
  const out: TimeSeriesPoint[] = [];
  for (let i = 0; i < n - 1; i++) {
    const dt = pts[i + 1].ts - pts[i].ts;
    const v0 = pts[i].value;
    const v1 = pts[i + 1].value;

    out.push(pts[i]);

    for (let s = 1; s < factor; s++) {
      const t  = s / factor;
      const t2 = t * t;
      const t3 = t2 * t;
      const h00 =  2 * t3 - 3 * t2 + 1;
      const h10 =      t3 - 2 * t2 + t;
      const h01 = -2 * t3 + 3 * t2;
      const h11 =      t3 -     t2;
      const value = h00 * v0 + h10 * (dt * m[i]) + h01 * v1 + h11 * (dt * m[i + 1]);
      out.push({ ts: pts[i].ts + dt * t, value });
    }
  }
  out.push(pts[n - 1]);
  return out;
}

/* ═══════════════════════════════════════════════════════════════════════
   Stage 4 — Dead-zone (suppress jitter below threshold)
   ═══════════════════════════════════════════════════════════════════════ */

function deadZoneFilter(
  pts: TimeSeriesPoint[],
  halfWidth: number,
): TimeSeriesPoint[] {
  if (halfWidth <= 0 || pts.length === 0) return pts;
  const out: TimeSeriesPoint[] = new Array(pts.length);
  out[0] = { ...pts[0] };
  let lastEmitted = pts[0].value;
  for (let i = 1; i < pts.length; i++) {
    const delta = pts[i].value - lastEmitted;
    if (Math.abs(delta) < halfWidth) {
      out[i] = { ts: pts[i].ts, value: lastEmitted };
    } else {
      out[i] = { ts: pts[i].ts, value: pts[i].value };
      lastEmitted = pts[i].value;
    }
  }
  return out;
}

/* ═══════════════════════════════════════════════════════════════════════
   Full pipeline — run all four stages in order
   ═══════════════════════════════════════════════════════════════════════ */

export function applyDsp(
  raw: TimeSeriesPoint[],
  preset: DspPreset,
): TimeSeriesPoint[] {
  if (!raw || raw.length === 0) return raw;

  let signal = raw;
  signal = medianFilter(signal, preset.glitchRadius);
  signal = emaSmooth(signal, preset.smoothingAlpha);
  signal = hermiteInterp(signal, preset.interpFactor);
  signal = deadZoneFilter(signal, preset.deadZone);
  return signal;
}
